/**
 * Network Status Hook
 * Detects online/offline status and provides offline mode indicator
 * Only active in development mode - has zero impact on production builds
 */

import { useState, useEffect } from 'react';
import { createLogger } from '../logger';
const logger = createLogger('Lib - useNetworkStatus');

interface NetworkStatus {
  isOnline: boolean;
  isOfflineMode: boolean;
}

/**
 * Hook to monitor network status and determine offline mode
 * @returns {NetworkStatus} Current network status
 */
const useNetworkStatus = (): NetworkStatus => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Only enable network monitoring in development
    if (!import.meta.env.VITE_DEV) {
      return;
    }

    const handleOnline = () => {
      logger.info('🌐 Network connection restored');
      setIsOnline(true);
    };

    const handleOffline = () => {
      logger.warn('📴 Network connection lost - switching to offline mode');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Offline mode is only active in development when offline
  const isOfflineMode = Boolean(!isOnline && import.meta.env.VITE_DEV);

  return { isOnline, isOfflineMode };
};

export { useNetworkStatus, type NetworkStatus };
